/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    Language,
    DetailsList,
    FilterType,
    FilteredColumn,
    SortedColumnInfo,
    Label,
} from '@eta/components';

import {
    DetailsListLayoutMode,
    SelectionMode,
    Selection,
    IObjectWithKey,
    TooltipHost,
    DefaultButton,
} from '@fluentui/react';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { connect, ConnectedProps } from 'react-redux';
import { useTranslation } from 'react-i18next';
import './POS.scss';
import {
    NumberOfShimmerLines,
    ReportIssue,
    UIHelpers,
} from '../../../helpers/UIHelpers';
import {
    CurrentDeviceListItemDto,
    CurrentDeviceListItemDtoQueryListResult,
    CurrentDevicesSortBy,
    DeviceStatus,
    ICurrentDeviceListItemDtoQueryListResult,
    SortDir,
} from '../../../Services/POS/PosService';
import { ReduxState } from '../../../common/reducers';
import { isEmptyText } from '../../../helpers/utils';
import { Actions, Defaults } from '../../../common/config/constants';
import { ServiceCreator } from '../../../Services/ClientProxy/ServiceFactory';
import { OverflowMenu } from '../../../common/component/OverflowMenu/OverflowMenu';
import { ClaimsHelper } from '../../../helpers/ClaimsHelper';

export interface DevicesGridProps {
    onActionClick: (action: string, item: CurrentDeviceListItemDto) => void;
    onSelectionChange?: (selectedItems: CurrentDeviceListItemDto[]) => boolean;
    selectedItems?: CurrentDeviceListItemDto[];
    onGridBind?: (devices: CurrentDeviceListItemDtoQueryListResult) => void;
    onGridChanged?: (
        pageIndex: number,
        filteredColumns: FilteredColumn[],
        sortedColumn?: SortedColumnInfo
    ) => void;
    forceResetFilter?: boolean;
    reload?: boolean;
    onRealod: () => void;
    taxpayerRIN?: string;
    model?: number;
}

interface ChangeParameters {
    pageIndex: number;
    pageSize: number;
    filteredColumn: FilteredColumn[];
    sortedColumn?: SortedColumnInfo;
}

const mapStateToProps = (state: ReduxState) => ({
    currentLanguage: state.reduxlanguage.language,
});

const connector = connect(mapStateToProps);
type LangProps = ConnectedProps<typeof connector>;

const DevicesGrid: FC<DevicesGridProps & LangProps> = (
    props: DevicesGridProps & LangProps
) => {
    const {
        selectedItems,
        reload,
        onRealod,
        onGridBind,
        currentLanguage: currentLanguageProps,
        onGridChanged,
        onActionClick,
        forceResetFilter,
        onSelectionChange,
    } = props;

    const [currentLanguage, setCurrentLanguage] =
        useState<Language>(currentLanguageProps);
    const lastChangeParameter = useRef<ChangeParameters>();

    const { t } = useTranslation(['pos', 'common']);
    const [filters, setFilters] = useState<FilteredColumn[]>([]);
    const [pageSize, setPageSize] = useState<number>(Defaults.PageSize);
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [enableShimmer, setEnableShimmer] = useState<boolean>(false);

    const [items, setItems] =
        useState<ICurrentDeviceListItemDtoQueryListResult>();
    const maxPageSize = 10;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const PredefinedMobileModel = (window as any)
        .REACT_APP_PredefinedMobileModel;

    const statusFilters = [
        {
            key: 'Pending',
            text: t('Pending'),
        },
        {
            key: 'In-active',
            text: t('Inactive'),
        },
        {
            key: 'Active',
            text: t('Active'),
        },
        {
            key: 'Retiring',
            text: t('Retiring'),
        },
        {
            key: 'Retired',
            text: t('Retired'),
        },
    ];

    const LoadData = async (
        pageIndex: number,
        ps: number,
        filteredColumns?: FilteredColumn[],
        sortedColumn?: SortedColumnInfo
    ) => {
        try {
            setEnableShimmer(true);
            let sort;
            const dir = sortedColumn?.isSortDesc ? SortDir.Desc : SortDir.Asc;
            switch (sortedColumn?.key) {
                case 'activeFrom':
                    sort = CurrentDevicesSortBy.ActiveFrom;
                    break;
                case 'activeto':
                    sort = CurrentDevicesSortBy.ActiveTo;
                    break;
                case 'LastSentReceipt':
                    sort = CurrentDevicesSortBy.LastSentReceiptDate;
                    break;
                default:
                    break;
            }
            const name = UIHelpers.getFilterValue('name', filteredColumns);
            const serial = UIHelpers.getFilterValue(
                'serialNumber',
                filteredColumns
            );
            const modelName = UIHelpers.getFilterValue(
                'modelName',
                filteredColumns
            );
            const vendor = UIHelpers.getFilterValue(
                'vendorName',
                filteredColumns
            );
            const status = UIHelpers.getFilterValue('status', filteredColumns);
            const branch = UIHelpers.getFilterValue('branch', filteredColumns);
            const formatedAddress = UIHelpers.getFilterValue(
                'formatedAddress',
                filteredColumns
            );
            const branchCode = UIHelpers.getFilterValue(
                'branchNumber',
                filteredColumns
            );
            let code;
            if (!isEmptyText(branchCode)) {
                const val = Number(branchCode);
                code = Number.isNaN(val) ? -1 : val;
            }

            const index = statusFilters?.findIndex(
                (i: { key: any }) => i.key === status
            );
            const dStatus = Object.values(DeviceStatus)[index ?? 0];
            // // eslint-disable-next-line no-nested-ternary
            // const dStatus = status
            //     ? // eslint-disable-next-line no-nested-ternary
            //       status === 'Active'
            //         ? DeviceStatus.Active
            //         : status === 'Pending'
            //         ? DeviceStatus.Pending
            //         : DeviceStatus.InActive
            //     : undefined;

            const client = ServiceCreator.getPosManagementPublicClient();
            const response = await client.getCurrentDevices(
                undefined,
                vendor,
                undefined,
                modelName,
                name,
                branch,
                formatedAddress,
                serial,
                code,
                undefined,
                undefined,
                dStatus,
                undefined,
                pageIndex,
                ps,
                sort,
                dir
            );
            if (onGridBind) onGridBind(response);
            setItems(response);
            onRealod();
            setEnableShimmer(false);
            // eslint-disable-next-line no-empty
        } catch (exception) {}
    };

    useEffect(() => {
        if (currentLanguage.lang !== currentLanguageProps.lang) {
            setCurrentLanguage(currentLanguageProps);
        }
    }, [currentLanguageProps, currentLanguage]);

    useEffect(() => {
        const performLoading = async () => {
            await LoadData(currentPage, pageSize, filters);
        };
        if (reload) {
            performLoading();
        }
    }, [reload]);

    useEffect(() => {
        if (
            currentLanguage.lang !== '' &&
            currentLanguage.lang !== currentLanguageProps.lang
        ) {
            const performLoading = async () => {
                await LoadData(currentPage, pageSize, filters);
            };
            performLoading();
            setCurrentLanguage(currentLanguageProps);
        }
    }, [currentLanguageProps, currentLanguage]);

    const gridColumns = [
        {
            key: 'name',
            name: t('mName'),
            fieldName: 'name',
            nameAsJsx: (
                <>
                    {t('dName')} /
                    <br />
                    {t('dSerial')}
                </>
            ),
            minWidth: 120,
            maxWidth: 120,
            isResizable: true,
            isSorted: false,
            isSortedDescending: false,
            isRowHeader: true,
            filterOption: {
                type: FilterType.Text,
                placeholder: '',
                fieldNames: [
                    {
                        fieldName: 'name',
                        displayName: `${t('dName')} ${t('fragment')}`,
                    },
                    {
                        fieldName: 'serialNumber',
                        displayName: `${t('dSerial')} ${t('fragment')}`,
                    },
                ],
            },
            onRender: ({
                name,
                serialNumber,
                id,
            }: CurrentDeviceListItemDto) => {
                return (
                    <>
                        <span>
                            <NavLink
                                exact
                                className="navLink"
                                to={`/TaxpayerProfile/pos/devices/${id}`}
                            >
                                {name}
                            </NavLink>
                        </span>
                        <div className="griCellSubTitle">{serialNumber}</div>
                    </>
                );
            },
        },
        {
            key: 'vendorName',
            name: t('mVendor'),
            fieldName: 'vendorName',
            nameAsJsx: (
                <>
                    {t('dModel')} /
                    <br />
                    {t('dVendor')}
                </>
            ),
            minWidth: 120,
            maxWidth: 120,
            isResizable: true,
            isSorted: false,
            isSortedDescending: false,
            isRowHeader: true,
            filterOption: {
                type: FilterType.Text,
                placeholder: '',
                fieldNames: [
                    {
                        fieldName: 'modelName',
                        displayName: `${t('dModel')} ${t('fragment')}`,
                    },
                    {
                        fieldName: 'vendorName',
                        displayName: `${t('dVendor')} ${t('fragment')}`,
                    },
                ],
            },
            onRender: ({ modelName, vendorName }: CurrentDeviceListItemDto) => {
                return (
                    <div className="horizontal">
                        <span>
                            <div className="griCellTitleGray">{modelName}</div>
                            <div className="griCellSubTitle">{vendorName}</div>
                        </span>
                    </div>
                );
            },
        },
        {
            key: 'branch',
            name: t('branch'),
            fieldName: 'branch',
            nameAsJsx: (
                <>
                    {t('branch')} /
                    <br />
                    {t('address')}
                </>
            ),
            minWidth: 120,
            maxWidth: 120,
            isResizable: true,
            isSorted: false,
            isSortedDescending: false,
            isRowHeader: true,
            filterOption: {
                type: FilterType.Text,
                placeholder: '',
                fieldNames: [
                    {
                        fieldName: 'branchNumber',
                        displayName: `${t('branchCode')}`,
                    },
                    {
                        fieldName: 'branch',
                        displayName: `${t('branchName')} ${t('fragment')}`,
                    },
                ],
            },
            onRender: ({
                branch,
                formatedAddress,
                branchNumber,
            }: CurrentDeviceListItemDto) => {
                return (
                    <>
                        <div className="griCellTitleGray">
                            {`${branchNumber} | ${branch ?? ''}`}
                        </div>
                        <div className="griCellSubTitle">{formatedAddress}</div>
                    </>
                );
            },
        },
        {
            key: 'LastSentReceipt',
            name: t('lastReceiptDate'),
            fieldName: 'lastReceiptDate',
            nameAsJsx: (
                <>
                    {t('last')}
                    <br />
                    {t('sentReceipt')}
                </>
            ),
            minWidth: 70,
            maxWidth: 70,
            isResizable: true,
            isSorted: true,
            allowSorting: true,
            isRowHeader: true,
            data: 'string',
            isPadded: true,
            onRender: ({ lastReceiptDate }: CurrentDeviceListItemDto) => {
                return (
                    <div className="horizontal">
                        <span>
                            <div className="griCellTitleGray">
                                {UIHelpers.getLocalizedDateTime(
                                    'Date',
                                    lastReceiptDate,
                                    currentLanguageProps.lang
                                )}
                            </div>
                        </span>
                    </div>
                );
            },
        },
        {
            key: 'activeFrom',
            name: t('activeFrom'),
            fieldName: 'activeFrom',
            nameAsJsx: (
                <>
                    {t('active')}
                    <br />
                    {t('from')}
                </>
            ),
            minWidth: 70,
            maxWidth: 70,
            isResizable: true,
            isSorted: false,
            isSortedDescending: false,
            allowSorting: true,
            isRowHeader: true,
            data: 'string',
            isPadded: true,
            onRender: ({ activeFrom, status }: CurrentDeviceListItemDto) => {
                return (
                    <div className="horizontal">
                        <div className="griCellTitleGray">
                            <span className={status}>
                                {UIHelpers.getLocalizedDateTime(
                                    'Date',
                                    activeFrom,
                                    currentLanguageProps.lang
                                )}
                            </span>
                        </div>
                    </div>
                );
            },
        },
        {
            key: 'activeto',
            name: t('activeTo'),
            fieldName: 'activeTo',
            nameAsJsx: (
                <>
                    {t('active')}
                    <br />
                    {t('to')}
                </>
            ),
            minWidth: 70,
            maxWidth: 70,
            isResizable: true,
            isSorted: false,
            allowSorting: true,
            isRowHeader: true,
            data: 'string',
            isPadded: true,
            onRender: ({ activeTo, status }: CurrentDeviceListItemDto) => {
                return (
                    <div className="horizontal">
                        <div className="griCellTitleGray">
                            <span className={status}>
                                {UIHelpers.getLocalizedDateTime(
                                    'Date',
                                    activeTo,
                                    currentLanguageProps.lang
                                )}
                            </span>
                        </div>
                    </div>
                );
            },
        },
        {
            key: 'status',
            name: t('status'),
            fieldName: 'status',
            minWidth: 70,
            maxWidth: 70,
            isResizable: false,
            isSorted: false,
            isRowHeader: true,
            filterOption: {
                placeholder: t('selectStatus'),
                type: FilterType.Enum,
                options: statusFilters,
            },

            onRender: ({ status }: CurrentDeviceListItemDto) => {
                return (
                    <div className="horizontal">
                        <span className={status}>{t(status ?? '')}</span>
                    </div>
                );
            },
        },
        {
            key: 'readyToSubmit',
            name: t('readyToSubmit'),
            nameAsJsx: (
                <>
                    {t('readyToSubmit1')}
                    <br />
                    {t('readyToSubmit2')}
                </>
            ),
            fieldName: 'readyToSubmit',
            minWidth: 60,
            maxWidth: 200,
            isResizable: true,
            onRender: (item: CurrentDeviceListItemDto) => {
                const submitStatus = UIHelpers.getNotSubmitReason(
                    item,
                    item.status === DeviceStatus.Active
                );
                const { canSubmit, noSubmitReason } = submitStatus;
                const { branchStartDate, branchCeaseDate } = item;
                const isActiveBranch = UIHelpers.checkBranchActivity(
                    branchStartDate,
                    branchCeaseDate
                );
                return (
                    <>
                        <div
                            className={
                                canSubmit && isActiveBranch
                                    ? 'canSubmit'
                                    : 'noSubmit'
                            }
                        >
                            <span>
                                {t(canSubmit && isActiveBranch ? 'yes' : 'no')}
                            </span>
                        </div>
                        {noSubmitReason && isActiveBranch && (
                            <div>
                                <Label
                                    title={t(noSubmitReason)}
                                    className="noSubmit"
                                >
                                    {t(noSubmitReason)}
                                </Label>
                            </div>
                        )}
                        {!isActiveBranch && (
                            <div>
                                <Label
                                    title={t('inactiveBranch')}
                                    className="noSubmit"
                                >
                                    {t('branchIsNotActive')}
                                </Label>
                            </div>
                        )}
                    </>
                );
            },
        },
        {
            key: 'action',
            name: '',
            minWidth: 20,
            maxWidth: 20,
            isResizable: false,
            data: 'string',
            isPadded: true,
            isSorted: false,
            isSortedDescending: false,
            isRowHeader: true,
            onRender: (item: CurrentDeviceListItemDto) => {
                const { status, modelName, isStatusUpdatedByAdmin } = item;
                const canSubmitRerport = ClaimsHelper.canSubmitComplaints();
                const IsRetired = item?.status === DeviceStatus.Retired;
                const reportItem = {
                    key: 'ReportIssue',
                    className: 'actionButton',
                    name: t('ReportIssue'),
                    iconProps: { iconName: 'FileComment' },
                    onClick: () => {
                        ReportIssue(item.id);
                    },
                    disabled:
                        item?.status === DeviceStatus.Retiring ||
                        item?.status === DeviceStatus.Retired,
                };

                const RetireItem = {
                    key: 'Retire',
                    className: 'actionButton',
                    name: t(
                        item?.status === DeviceStatus.Retiring
                            ? 'revert'
                            : 'Retire'
                    ),
                    iconProps: {
                        iconName:
                            item?.status === DeviceStatus.Retiring
                                ? 'EmptyRecycleBin'
                                : 'RecycleBin',
                    },
                    onClick: () => {
                        onActionClick('Retire', item);
                    },
                    disabled: item?.modelName === PredefinedMobileModel,
                };
                const active = status !== DeviceStatus.InActive;
                let menuItems = [
                    {
                        key: 'Edit',
                        className: 'actionButton',
                        iconProps: {
                            iconName: 'Edit',
                        },
                        disabled:
                            item?.status === DeviceStatus.Retiring ||
                            item?.status === DeviceStatus.Retired,
                        name: t('edit'),
                        onClick: () => {
                            onActionClick(Actions.Edit, item);
                        },
                    },
                    {
                        key: 'Deactivate',
                        className: 'actionButton',
                        disabled:
                            isStatusUpdatedByAdmin === true ||
                            item?.status === DeviceStatus.Retiring ||
                            item?.status === DeviceStatus.Retired,
                        onRender: () => (
                            <TooltipHost
                                content={
                                    isStatusUpdatedByAdmin === true
                                        ? t('DeactivatePOSTooltip')
                                        : ''
                                }
                                calloutProps={{ calloutMaxWidth: 200 }}
                                className="POSDevice_DeactivateButton_Tooltip"
                            >
                                <DefaultButton
                                    className="commandAction deactivateButton"
                                    disabled={
                                        isStatusUpdatedByAdmin === true ||
                                        item?.status ===
                                            DeviceStatus.Retiring ||
                                        item?.status === DeviceStatus.Retired
                                    }
                                    onClick={() => {
                                        onActionClick(
                                            active
                                                ? Actions.Deactivate
                                                : Actions.Reactivate,
                                            item
                                        );
                                    }}
                                    iconProps={{
                                        iconName: active
                                            ? 'StatusCircleBlock'
                                            : 'PlayResume',
                                    }}
                                >
                                    {t(active ? 'deactivate' : 'reactivate')}
                                </DefaultButton>
                            </TooltipHost>
                        ),
                    },
                    {
                        key: 'Secrets',
                        className: 'actionButton',
                        iconProps: {
                            iconName: 'Undo',
                        },
                        name: t('reGenerate'),
                        disabled:
                            item?.status === DeviceStatus.Retiring ||
                            item?.status === DeviceStatus.Retired,
                        onClick: () => {
                            onActionClick(Actions.RegenerateSecrets, item);
                        },
                    },
                ];
                if (modelName === PredefinedMobileModel) {
                    menuItems = menuItems.filter((i) => i.key === 'Deactivate');
                }
                if (canSubmitRerport) menuItems.push(reportItem);
                if (!IsRetired) menuItems.push(RetireItem);
                return <OverflowMenu overflowItems={menuItems} />;
            },
        },
    ];

    const onChanged = (
        pageIndex: number,
        filteredColumns: FilteredColumn[],
        sortedColumn?: SortedColumnInfo
    ) => {
        setFilters(filteredColumns);
        setCurrentPage(pageIndex + 1);
        LoadData(pageIndex + 1, pageSize, filteredColumns, sortedColumn);
        if (onGridChanged)
            onGridChanged(pageIndex, filteredColumns, sortedColumn);
        return false;
    };

    const selection = useMemo(
        () =>
            new Selection<IObjectWithKey>({
                selectionMode: SelectionMode.multiple,
                onSelectionChanged: () => {
                    const selected = selection.getSelection();
                    const lenNoDefined = selected?.filter(
                        (s) => s === undefined
                    );
                    if (
                        lenNoDefined.length === selected.length &&
                        lenNoDefined.length !== 0
                    ) {
                        return;
                    }
                    let needReload = false;
                    if (onSelectionChange) {
                        needReload = onSelectionChange(
                            selected.map((s) => s as CurrentDeviceListItemDto)
                        );
                    }
                    if (needReload && onChanged) {
                        const pi =
                            Math.ceil(
                                ((lastChangeParameter?.current?.pageSize ?? 0) *
                                    ((lastChangeParameter?.current?.pageIndex ??
                                        0) +
                                        1)) /
                                    maxPageSize
                            ) - 1 ?? 0;
                        onChanged(pi, []);
                    }
                },
                getKey: (item) => {
                    const device = item as CurrentDeviceListItemDto;
                    return device?.id?.toString() ?? '';
                },
            }),
        []
    );

    useEffect(() => {
        try {
            if (selectedItems) {
                selectedItems.forEach(({ id }) => {
                    selection.setKeySelected(id?.toString() ?? '', true, false);
                });
            }
            // eslint-disable-next-line no-empty
        } catch {}
    }, [selectedItems]);

    const onNbItemPerPageChanged = (nbItemPerPage: number) => {
        setPageSize(nbItemPerPage);
        setCurrentPage(1);
        LoadData(1, nbItemPerPage, filters);
        return true;
    };

    return (
        <DetailsList
            className="Devicesgrid"
            columns={gridColumns ?? []}
            items={items?.data ?? []}
            onChanged={(pageIndex, columns, sort) => {
                return onChanged(pageIndex, columns, sort);
            }}
            forceResetFilter={forceResetFilter}
            layoutMode={DetailsListLayoutMode.justified}
            selection={selection}
            selectionPreservedOnEmptyClick
            selectionMode={SelectionMode.multiple}
            shimmerLines={NumberOfShimmerLines}
            enableShimmer={!items || enableShimmer}
            noItemsPlaceholder={<span>{t('no-records')}</span>}
            totalItemCount={items?.totalCount}
            labels={{
                resultPerPage: t('common:nbItemPerPage'),
                totalRecord: t('common:totalRecord'),
            }}
            paginationSetting={{
                nbItemPerPage: pageSize,
                nbPageShown: 5,
                currentPage: currentPage - 1,
                totalPage: items?.pagesCount || 0,
            }}
            onNbItemPerPageChanged={onNbItemPerPageChanged}
        />
    );
};

export default connector(DevicesGrid);
